/**
 * PreCreditPanel — buyer-facing summary of per-seller pre-credit pools.
 *
 * Mounted as a new section ("pre-credit") in BuyerPayments. Lists every
 * seller the buyer has deposited cash with, the current balance, the
 * deposit history, and any redemptions against past invoices.
 *
 * Empty state surfaces the buyer's already-connected sellers (verqo is
 * a closed network — no public "browse" is appropriate). Each seller
 * shows up with a one-click "Deposit" CTA so the buyer can fund any
 * existing relationship in two clicks total.
 */
import { useEffect, useState } from "react";
import { Landmark, Loader2, ArrowDownLeft, RefreshCw, Info, Plus, Building2 } from "lucide-react";
import api, { money } from "../lib/api";
import { Button } from "./ui/button";
import PreCreditDepositDialog from "./PreCreditDepositDialog";

export default function PreCreditPanel({ walletBalance = 0, onChanged }) {
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [depositTarget, setDepositTarget] = useState(null);
  // Connected sellers — only populated when the buyer has no pools yet
  // (lazy-load just for the empty-state list).
  const [connections, setConnections] = useState(null);

  const load = async () => {
    setRows(null);
    try {
      const { data } = await api.get("/buyer/pre-credits");
      setRows(data?.items || []);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  // When the user has NO existing pools, pull their connected sellers
  // so the empty state can offer one-click deposit on each. We only
  // fetch this lazily — for buyers with active pools, it's not needed.
  useEffect(() => {
    if (rows === null || rows.length > 0 || connections !== null) return;
    (async () => {
      try {
        const { data } = await api.get("/buyer/connections");
        setConnections(Array.isArray(data) ? data : []);
      } catch { setConnections([]); }
    })();
  }, [rows, connections]);

  const totalBalance = (rows || []).reduce((s, r) => s + Number(r.balance || 0), 0);
  const totalDeposited = (rows || []).reduce((s, r) => {
    return s + (r.deposit_history || []).reduce((ss, d) => ss + Number(d.amount || 0), 0);
  }, 0);
  const totalRedeemed = Math.max(0, totalDeposited - totalBalance);

  if (rows === null) {
    return (
      <div className="py-12 text-center text-foreground/55 inline-flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin"/>Loading pre-credit pools…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pre-credit-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs mono uppercase tracking-[0.2em] text-foreground/50">Pre-credit pools</div>
          <h2 className="display text-4xl tracking-[-0.02em] leading-[1.05] max-w-2xl mt-2">
            Cash, parked with sellers you trust.
          </h2>
          <p className="text-sm text-foreground/55 mt-2.5 max-w-xl">
            Drop money in a per-seller pool and we auto-apply it on your next invoice
            with them — ahead of every other payment method.
          </p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden border border-border">
        <div className="bg-background p-5">
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/50 inline-flex items-center gap-1.5">
            <Landmark className="w-3 h-3"/>
            On deposit
          </div>
          <div className="display text-3xl mt-1.5 tabular-nums" data-testid="pc-summary-balance">
            {money(totalBalance)}
          </div>
          <div className="text-[11px] text-foreground/55 mt-0.5">
            Across {(rows || []).length} seller{(rows || []).length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="bg-background p-5">
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/50 inline-flex items-center gap-1.5">
            <ArrowDownLeft className="w-3 h-3"/>
            Deposited (lifetime)
          </div>
          <div className="display text-3xl mt-1.5 tabular-nums" data-testid="pc-summary-deposited">
            {money(totalDeposited)}
          </div>
        </div>
        <div className="bg-background p-5">
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/50 inline-flex items-center gap-1.5">
            <RefreshCw className="w-3 h-3"/>
            Redeemed
          </div>
          <div className="display text-3xl mt-1.5 tabular-nums" data-testid="pc-summary-redeemed">
            {money(totalRedeemed)}
          </div>
        </div>
      </div>

      {/* Empty state — surface the buyer's already-connected sellers
          so they can fund any active relationship in 1 click. verqo is
          a closed network so we never push toward public "browse". */}
      {rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border p-8" data-testid="pc-empty">
          <div className="text-center max-w-md mx-auto">
            <Landmark className="w-7 h-7 mx-auto text-foreground/40"/>
            <div className="display text-xl tracking-tight mt-3">No pre-credit pools yet</div>
            <p className="text-[13px] text-foreground/60 mt-2 leading-relaxed">
              Park cash with any seller you're already connected to. It auto-applies
              to your next invoice with them — before card, wallet, or ACH. The
              seller can refund you anytime via a credit memo.
            </p>
          </div>

          {connections === null ? (
            <div className="mt-6 text-center text-foreground/50 text-[12.5px] inline-flex items-center gap-1.5 justify-center w-full">
              <Loader2 className="w-3 h-3 animate-spin"/>Loading your sellers…
            </div>
          ) : connections.length === 0 ? (
            <div className="mt-6 text-center text-foreground/55 text-[12.5px]">
              You're not connected to any sellers yet. Once a seller invites you
              (or you accept an invite), they'll show up here.
            </div>
          ) : (
            <div className="mt-6 rounded-xl bg-background border border-border overflow-hidden"
                 data-testid="pc-empty-connections">
              <div className="px-4 py-2.5 text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 border-b border-border bg-secondary/40">
                Sellers you're connected to · {connections.length}
              </div>
              {connections.slice(0, 8).map(c => {
                const sellerId = c.tenant_owner_user_id || c.seller_id;
                const sellerName = c.tenant_name || c.seller_name || c.name || "Seller";
                if (!sellerId) return null;
                return (
                  <div key={c.id || sellerId}
                       className="px-4 py-3 flex items-center gap-3 border-b border-border last:border-b-0"
                       data-testid={`pc-conn-${sellerId}`}>
                    <div className="w-8 h-8 rounded-full bg-foreground/5 grid place-items-center shrink-0">
                      <Building2 className="w-3.5 h-3.5 text-foreground/65"/>
                    </div>
                    <div className="flex-1 min-w-0 text-[13.5px] truncate">{sellerName}</div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full shrink-0"
                      onClick={() => setDepositTarget({ id: sellerId, name: sellerName })}
                      data-testid={`pc-conn-deposit-${sellerId}`}
                    >
                      <Plus className="w-3 h-3 mr-1"/>Deposit
                    </Button>
                  </div>
                );
              })}
              {connections.length > 8 && (
                <div className="px-4 py-2 text-[11px] text-foreground/45 text-center border-t border-border bg-secondary/30">
                  +{connections.length - 8} more — search via Suppliers
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Per-seller rows */}
      {rows.length > 0 && (
        <div className="rounded-2xl border border-border overflow-hidden bg-background">
          {rows.map(row => {
            const isOpen = expanded === row.id;
            const dHist = row.deposit_history || [];
            const rHist = row.redemption_history || [];
            return (
              <div key={row.id} className="border-b border-border last:border-b-0" data-testid={`pc-row-${row.id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : row.id)}
                  className="w-full px-5 py-4 flex items-center gap-4 hover:bg-secondary/40 transition-colors text-left"
                >
                  <div className="w-9 h-9 rounded-full bg-foreground/5 grid place-items-center shrink-0">
                    <Landmark className="w-4 h-4 text-foreground/65"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[14px] truncate">{row.seller_name || "Seller"}</div>
                    <div className="text-[11px] text-foreground/55 mt-0.5">
                      {dHist.length} deposit{dHist.length === 1 ? "" : "s"}
                      {rHist.length > 0 && ` · ${rHist.length} redemption${rHist.length === 1 ? "" : "s"}`}
                      {row.last_deposit_at && ` · last ${new Date(row.last_deposit_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="display text-xl tabular-nums">{money(row.balance || 0)}</div>
                    <div className="text-[10.5px] text-foreground/50">balance</div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full shrink-0"
                    onClick={(e) => { e.stopPropagation(); setDepositTarget({ id: row.seller_id, name: row.seller_name }); }}
                    data-testid={`pc-add-${row.seller_id}`}
                  >
                    <Plus className="w-3 h-3 mr-1"/>Add
                  </Button>
                </button>

                {isOpen && (
                  <div className="px-5 pb-5 -mt-1 grid sm:grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10.5px] mono uppercase tracking-[0.15em] text-foreground/55 mb-2">
                        Deposits
                      </div>
                      {dHist.length === 0 ? (
                        <div className="text-[12px] text-foreground/50">No deposits yet.</div>
                      ) : (
                        <ul className="space-y-1.5 text-[12.5px]">
                          {dHist.slice().reverse().slice(0, 8).map(d => (
                            <li key={d.id || d.at} className="flex items-start gap-2"
                                data-testid={`pc-dep-${d.id || d.at}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0"/>
                              <div className="flex-1">
                                <div className="flex justify-between">
                                  <span>{new Date(d.at).toLocaleDateString()}</span>
                                  <span className="tabular-nums">+{money(d.amount)}</span>
                                </div>
                                {d.note && (
                                  <div className="text-[10.5px] text-foreground/55 mt-0.5">{d.note}</div>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <div className="text-[10.5px] mono uppercase tracking-[0.15em] text-foreground/55 mb-2">
                        Redemptions
                      </div>
                      {rHist.length === 0 ? (
                        <div className="text-[12px] text-foreground/50 flex items-start gap-1.5">
                          <Info className="w-3 h-3 mt-0.5"/>Auto-applies on your next invoice with this seller.
                        </div>
                      ) : (
                        <ul className="space-y-1.5 text-[12.5px]">
                          {rHist.slice().reverse().slice(0, 8).map((r, idx) => (
                            <li key={`${r.invoice_id}-${idx}`} className="flex items-start gap-2"
                                data-testid={`pc-red-${r.invoice_id}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-foreground/30 mt-1.5 shrink-0"/>
                              <div className="flex-1">
                                <div className="flex justify-between">
                                  <span className="mono">{r.invoice_code || r.invoice_id?.slice(0, 8)}</span>
                                  <span className="tabular-nums text-foreground/65">−{money(r.amount)}</span>
                                </div>
                                <div className="text-[10.5px] text-foreground/45">
                                  {new Date(r.at).toLocaleDateString()}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <PreCreditDepositDialog
        open={!!depositTarget}
        onClose={() => setDepositTarget(null)}
        seller={depositTarget}
        walletBalance={walletBalance}
        onSuccess={() => { load(); onChanged?.(); }}
      />
    </div>
  );
}
